import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BadgeCheck, Building2, Handshake, Loader2, MousePointerClick, PhoneCall, Quote, ShieldCheck, Star } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { PublicRequestCard } from "@/components/PublicRequestCard";
import { startUnlock } from "@/lib/checkout";
import { TIPI_RICERCA, normTipo, colonnaTipo } from "@/data/formOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loadUnlocked = () => {
  try {
    return JSON.parse(localStorage.getItem("cm_unlocked") || "{}");
  } catch {
    return {};
  }
};

export default function Richieste() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(null);
  const [unlocked, setUnlocked] = useState(loadUnlocked);
  const [filterCategoria, setFilterCategoria] = useState("tutte");
  const [filterProvincia, setFilterProvincia] = useState("tutte");
  const [filterCitta, setFilterCitta] = useState("");
  const [reviews, setReviews] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/requests`);
      setData(data);
    } catch {
      toast.error("Errore nel caricamento delle richieste");
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("pagamento") === "annullato") {
      toast.info("Pagamento annullato");
      window.history.replaceState({}, "", "/richieste");
      return;
    }
    if (params.get("pagamento") !== "ok" || !sessionId) return;
    window.history.replaceState({}, "", "/richieste");
    try {
      const { data } = await axios.get(`${API}/public/status/${sessionId}`);
      if (data.payment_status !== "paid") {
        toast.info("Pagamento in elaborazione, riprova tra pochi secondi");
        return;
      }
      if (data.contatto) {
        const map = loadUnlocked();
        map[data.contatto.request_id] = { ...data.contatto, u: sessionId };
        localStorage.setItem("cm_unlocked", JSON.stringify(map));
        setUnlocked(map);
        toast.success("Match sbloccato! Lo trovi anche nella tua email.");
      }
      load();
    } catch {
      toast.error("Errore nella verifica del pagamento");
    }
  };

  useEffect(() => {
    handleReturn().then(load);
    axios.get(`${API}/reviews/summary`).then(({ data }) => setReviews(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlock = async (r) => {
    setUnlocking(r.id);
    try {
      await startUnlock(r.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore avvio pagamento");
      setUnlocking(null);
    }
  };

  const requests = useMemo(
    () =>
      (data?.requests || []).map((r) =>
        unlocked[r.id] ? { ...r, sbloccata: true, ...unlocked[r.id] } : r
      ),
    [data, unlocked]
  );

  const provinceDisponibili = useMemo(
    () => [...new Set(requests.map((r) => r.provincia))].sort(),
    [requests]
  );

  const cittaDisponibili = useMemo(
    () => [...new Set(requests.map((r) => r.comune).filter(Boolean))].sort(),
    [requests]
  );

  const filtered = useMemo(
    () =>
      requests.filter((r) => {
        if (filterCategoria !== "tutte") {
          const cat = r.tipo_ricerca || "";
          if (filterCategoria === "acquisto" && !cat.includes("Acquisto")) return false;
          if (filterCategoria === "lungo" && !cat.includes("Lungo")) return false;
          if (filterCategoria === "breve" && !cat.includes("Breve") && !cat.includes("Turistico")) return false;
        }
        if (filterProvincia !== "tutte" && r.provincia !== filterProvincia) return false;
        if (filterCitta && !(r.comune || "").toLowerCase().includes(filterCitta.toLowerCase())) return false;
        return true;
      }),
    [requests, filterCategoria, filterProvincia, filterCitta]
  );

  const COLONNE = [
    {
      key: "acquisto", title: "Acquisto", sub: "Residenziale · Business · Studenti · Artigianale",
      bg: "bg-[#0A192F]/[0.03]", border: "border-[#0A192F]/20", badge: "bg-[#0A192F] text-white", text: "text-[#0A192F]",
    },
    {
      key: "breve", title: "Affitto breve", sub: "Vacanze · Turismo · Residenziale",
      bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-500 text-white", text: "text-amber-700",
    },
    {
      key: "lungo", title: "Affitto lungo termine", sub: "Residenziale · Studenti · Lavoro",
      bg: "bg-emerald-50", border: "border-emerald-200", badge: "bg-emerald-500 text-white", text: "text-emerald-700",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
          <a href="/" className="flex items-center gap-3" data-testid="richieste-logo-link">
            <LogoMark size={38} />
            <div className="leading-tight">
              <p className="font-display font-extrabold">La casa cerca te!</p>
              <p className="text-[11px] text-slate-400">powered by Casamatch.org</p>
            </div>
          </a>
          <a
            href="/"
            className="inline-flex items-center rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-semibold transition-colors"
          >
            ← Torna alla home
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="richieste-page">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 mb-3">
            La bacheca al contrario
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A192F]">
            Le richieste attive
          </h1>
          {reviews && reviews.count > 0 && (
            <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-1.5 text-sm font-bold text-[#0A192F]" data-testid="richieste-reviews-badge">
              <span className="text-[#10B981]">★ {reviews.avg}</span> · {reviews.count} {reviews.count === 1 ? "recensione" : "recensioni"} di chi ha già sbloccato
            </p>
          )}
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl text-[15px]">
            Ogni scheda è una persona vera che ha descritto casa, zona e budget e chiede di essere
            contattata solo per la soluzione giusta. Hai quella casa? Sblocchi il contatto e la
            chiami tu. <strong className="text-[#0A192F]">Nessun annuncio pubblico: nessuno può scavalcarti.</strong>
          </p>
          <p className="mt-3 text-xs text-slate-400">
            Il contatto arriva subito a video e via email dopo il pagamento — e resta tuo per sempre.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10"
          data-testid="richieste-trust-row"
        >
          {[
            { icon: BadgeCheck, t: "Richieste vere, non curiosi", d: "Chi compila dichiara zona, budget e caratteristiche: sai già se puoi chiudere prima di pagare." },
            { icon: ShieldCheck, t: "Nessuno ti scavalca", d: "Non pubblichiamo annunci né immobili: il contatto esiste solo qui e lo vedi solo sbloccandolo." },
            { icon: Handshake, t: "Scatta il Match", d: "Dopo lo sblocco vi scrivete subito nella chat protetta e ricevi anche nome, WhatsApp ed email: la trattativa è tua, senza commissioni." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-2xl bg-white border border-slate-200/80 p-5 shadow-[0_8px_24px_rgba(10,25,47,0.04)]">
              <Icon className="w-5 h-5 text-[#10B981] mb-3" />
              <p className="text-sm font-bold text-[#0A192F]">{t}</p>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{d}</p>
            </div>
          ))}
        </motion.div>

        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mb-10 text-[13px] font-semibold text-slate-500">
          <span className="inline-flex items-center gap-2"><MousePointerClick className="w-4 h-4 text-[#10B981]" /> 1 · Trovi la richiesta giusta</span>
          <span className="inline-flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[#10B981]" /> 2 · Sblocchi con un pagamento sicuro</span>
          <span className="inline-flex items-center gap-2"><PhoneCall className="w-4 h-4 text-[#10B981]" /> 3 · Scatta il Match: chatti o la chiami su WhatsApp</span>
        </div>

        {reviews && reviews.latest.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mb-12"
            data-testid="richieste-reviews-section"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 mb-4">
              Chi ha già provato il matching dice
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {reviews.latest.map((rev, i) => (
                <div key={i} className="rounded-2xl bg-white border border-slate-200/80 p-5 shadow-[0_8px_24px_rgba(10,25,47,0.04)]" data-testid={`review-card-${i}`}>
                  <Quote className="w-5 h-5 text-emerald-200 mb-3" />
                  <div className="flex gap-0.5 mb-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= rev.rating ? "fill-[#10B981] text-[#10B981]" : "text-slate-200"}`} />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed italic">“{rev.testo}”</p>
                  <p className="text-xs font-bold text-[#0A192F] mt-3">{rev.nome}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-0.5">{rev.ruolo}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-8" data-testid="richieste-filters">
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { key: "tutte", label: "Tutte" },
              { key: "acquisto", label: "Acquisto", color: "bg-[#0A192F] text-white" },
              { key: "lungo", label: "Affitto lungo", color: "bg-emerald-500 text-white" },
              { key: "breve", label: "Affitto breve", color: "bg-amber-500 text-white" },
            ].map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setFilterCategoria(c.key)}
                data-testid={`richieste-filter-${c.key}`}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-all ${
                  filterCategoria === c.key
                    ? c.key === "tutte"
                      ? "bg-slate-700 text-white"
                      : c.color
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={filterProvincia}
              onChange={(e) => setFilterProvincia(e.target.value)}
              data-testid="richieste-filter-provincia"
              className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-[#0A192F] outline-none focus:border-[#10B981]"
            >
              <option value="tutte">Provincia: tutte</option>
              {provinceDisponibili.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <input
              type="text"
              value={filterCitta}
              onChange={(e) => setFilterCitta(e.target.value)}
              data-testid="richieste-filter-citta"
              placeholder="Città o comune..."
              list="richieste-citta"
              className="h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-[#0A192F] outline-none focus:border-[#10B981] placeholder:text-slate-400"
            />
            <datalist id="richieste-citta">
              {cittaDisponibili.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <button
              type="button"
              onClick={() => {
                setFilterCategoria("tutte");
                setFilterProvincia("tutte");
                setFilterCitta("");
              }}
              data-testid="richieste-filter-reset"
              className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-500 hover:border-slate-400 transition-colors"
            >
              Azzera filtri
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-3">{filtered.length} richieste trovate</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#10B981]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-3xl border border-slate-200">
            <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <p className="font-display font-bold text-[#0A192F] text-lg">Nessuna richiesta con questi filtri</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-testid="richieste-columns">
            {COLONNE.map((col) => {
              const rows = filtered.filter((r) => colonnaTipo(r.tipo_ricerca) === col.key);
              return (
                <div key={col.key} className={`rounded-3xl border p-4 ${col.bg} ${col.border}`} data-testid={`richieste-col-${col.key}`}>
                  <div className="px-2 pt-2 pb-4">
                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${col.badge}`}>{col.title}</span>
                    <p className={`text-[11px] mt-2 leading-snug ${col.text}`}>{col.sub}</p>
                    <p className="text-[11px] font-bold text-slate-500 mt-2">{rows.length} richieste</p>
                  </div>
                  <div className="space-y-4">
                    {rows.length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-8 bg-white rounded-2xl border border-dashed border-slate-200">
                        Nessuna richiesta qui per ora
                      </p>
                    ) : (
                      rows.map((r) => (
                        <PublicRequestCard key={r.id} r={r} onUnlock={unlock} unlocking={unlocking} />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
